import _ from '@lodash';
import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { showMessage } from 'app/store/fuse/messageSlice';
import LimitValueType from 'app/admin/optimizationCase/dto/LimitValueType';
import EditFormPage from 'modules/ui/editform/EditFormPage';
import EditFormTextField from 'modules/ui/editform/fields/EditFormTextField';
import EditFormSelectField from 'modules/ui/editform/fields/EditFormSelectField';
import EditFormCheckboxField from 'modules/ui/editform/fields/EditFormCheckboxField';
import EditFormGridComponent from 'modules/ui/editform/subForm/EditFormGridComponent';
import EditFormComponentMassSomethingField from 'app/admin/common/component/EditFormComponentMassSomethingField';
import StorageTransformerService from 'app/admin/workflow/service/StorageTransformerService';
import StorageAspectService from 'app/admin/aspect/service/StorageAspectService';
import AspectEditService from 'app/admin/aspect/service/AspectEditService';
import { useParams, withRouter } from 'react-router';
import OptimizationCaseEditService from '../service/OptimizationCaseEditService';
import CreateOptimizerLogButton from '../component/CreateOptimizerLogButton';
import OptimizationCaseStateButton from '../component/OptimizationCaseStateButton';
import OptimizationGoalType from '../dto/OptimizationGoalType';
import OptimCaseLoadAspectAttributes from '../component/OptimCaseLoadAspectAttributes';
import UserGroupService from 'app/admin/userGroup/service/UserGroupService';

const config = data => {
	const storageAspectService = new StorageAspectService();
	const storageTransformerService = new StorageTransformerService();

	return {
		key: 'optimizationCaseEditForm',
		topic: 'optimizationCase',
		service: OptimizationCaseEditService,
		tabList: initData => {
			return [{ key: 'basic' }, { key: 'domains' }, { key: 'stateLimits' }, { key: 'aspectLimits' }, { key: 'transformerLimits' }, { key: 'transformer_attributes' }];
		},
		fields: initData => {
			const fields = [
				{
					key: 'name',
					component: EditFormTextField,
					required: true,
					readOnly: data.id !== 'new'
				},
				{
					key: 'userGroup',
					component: EditFormSelectField,
					// multiple: true,
					items: () => UserGroupService.getListSelection()
				},
				{
					key: 'optimizer',
					component: EditFormSelectField,
					items: data.formData.optimizer
				},
				{
					key: 'optimizerLog',
					component: EditFormSelectField,
					items: data.formData.optimizerLog
				},
				{
					key: 'aspect',
					component: EditFormSelectField,
					items: data.formData.aspect
				},
				{
					key: 'transformer',
					component: EditFormSelectField,
					items: data.formData.transformer,
					// onChange: (value, field, methods) => { console.log(value) }
				},
				{
					key: 'imputedValue',
					component: EditFormTextField,
					type: 'number'
				},
				{
					key: 'optimizationGoal',
					component: EditFormSelectField,
					type: 'enum',
					items: Object.keys(OptimizationGoalType).map(key => { return { label: OptimizationGoalType[key], value: key }; })
				},
				{
					key: 'outputKey',
					component: EditFormSelectField,
					type: 'enum',
					filterKey: ['transformer', 'aspect'],
					items: formValues => {
						const transformer = formValues[0];
						const aspect = formValues[1];

						if (transformer != null)
							return new Promise(resolve => { storageTransformerService.getOutputKeysSelection(transformer).then(outputs => { resolve([...(_.isArray(outputs) ? outputs : [])]); }); });

						if (transformer == null && aspect != null)
							return new Promise(resolve => { storageAspectService.getOutputKeysSelection(aspect).then(inputs => { resolve([...(_.isArray(inputs) ? inputs : [])]); }); });

						return new Promise(resolve => resolve([]));
					}
				},
				{
					key: 'stepLimit',
					component: EditFormTextField,
					type: 'number'
				},
				{
					key: 'domains',
					tab: 'domains',
					component: EditFormGridComponent,
					defaultValue: {
						id: null,
						enabled: true,
						name: null,
						type: null,
						value: null,
						minValue: null,
						maxValue: null,
						stepCount: null
					},
					fields: [
						{
							key: 'enabled',
							component: EditFormCheckboxField
						},
						{
							key: 'name',
							component: EditFormSelectField,
							type: 'enum',
							filterKey: '^aspect',
							required: true,
							items: aspectId => {
								if (aspectId != null)
									return new Promise(resolve => { storageAspectService.getInputAttributesListSelection(aspectId).then(inputs => { resolve([...(_.isArray(inputs) ? inputs : [])]); }); });

								return new Promise(resolve => resolve([]));
							}
						},
						{
							key: 'type',
							component: EditFormSelectField,
							required: true,
							type: 'enum',
							items: [
								{ label: 'Variable', value: 'Variable' },
								{ label: 'ComponentMassFrac', value: 'ComponentMassFrac' },
								{ label: 'SpreadSheet', value: 'SpreadSheet' }
							]
						},
						{
							key: 'value',
							component: EditFormTextField,
							required: true,
							type: 'number',
							dependsOn: 'type',
							visibleIf: type => type === 'Variable'
						},
						{
							key: 'value',
							component: EditFormComponentMassSomethingField,
							dependsOn: 'type',
							visibleIf: type => type === 'ComponentMassFrac'
						},
						{
							key: 'minValue',
							component: EditFormTextField,
							required: true,
							dependsOn: 'type',
							visibleIf: type => type === 'Variable' || type === 'SpreadSheet' || type === null,
							type: 'number'
						},
						{
							key: 'minValue',
							component: EditFormComponentMassSomethingField,
							loadValueFrom: 'value',
							loadValueMultiplier: 0.9,
							dependsOn: 'type',
							visibleIf: type => type === 'ComponentMassFrac',
							enumByField: 'components'
						},
						{
							key: 'maxValue',
							component: EditFormTextField,
							required: true,
							dependsOn: 'type',
							visibleIf: type => type === 'Variable' || type === 'SpreadSheet' || type === null,
							type: 'number'
						},
						{
							key: 'maxValue',
							component: EditFormComponentMassSomethingField,
							loadValueFrom: 'value',
							loadValueMultiplier: 1.1,
							dependsOn: 'type',
							visibleIf: type => type === 'ComponentMassFrac',
							enumByField: 'components'
						},
						{
							key: 'stepCount',
							component: EditFormTextField,
							type: 'number',
							required: true
						}
					]
				},
				{
					key: 'stateLimits',
					tab: 'stateLimits',
					component: EditFormGridComponent,
					defaultValue: {
						id: null,
						name: null,
						type: null,
						boundaryType: null,
						boundary: null,
						value: null,
						factor: null,
						bias: null
					},
					fields: [
						// {
						// 	key: 'name',
						// 	component: EditFormTextField,
						// 	required: true
						// },
						{
							key: 'name',
							component: EditFormSelectField,
							required: true,
							type: 'enum',
							filterKey: '^aspect',
							items: aspectId => {
								if (aspectId != null) {
									return new Promise(resolve => {
										storageAspectService.getInputAttributesListSelection(aspectId).then(inputs => {
											resolve([...(_.isArray(inputs) ? inputs : [])]);
										});
									});
								}
								return new Promise(resolve => resolve([]));
							}
						},
						{
							key: 'type',
							component: EditFormSelectField,
							required: true,
							type: 'enum',
							items: [
								{ label: 'Constant', value: 'Constant' },
								{ label: 'Linear', value: 'Linear' },
								{ label: 'Exponential', value: 'Exponential' }
							]
						},
						{
							key: 'boundaryType',
							component: EditFormSelectField,
							required: true,
							type: 'enum',
							items: [
								{ label: 'Lower', value: 'Lower' },
								{ label: 'Upper', value: 'Upper' }
							]
						},
						{
							key: 'boundary',
							component: EditFormTextField,
							type: 'number'
						},
						{
							key: 'value',
							component: EditFormTextField,
							type: 'number',
							dependsOn: 'type',
							visibleIf: type => type === 'Constant'
						},
						{
							key: 'factor',
							component: EditFormTextField,
							type: 'number',
							dependsOn: 'type',
							visibleIf: type => (type === 'Linear' || type === 'Exponential')
						},
						{
							key: 'bias',
							component: EditFormTextField,
							type: 'number',
							dependsOn: 'type',
							visibleIf: type => (type === 'Linear' || type === 'Exponential')
						}
					]
				},
				{
					key: 'aspectLimits',
					tab: 'aspectLimits',
					component: EditFormGridComponent,
					defaultValue: {
						id: null,
						name: null,
						type: null,
						boundaryType: null,
						boundary: null,
						value: null,
						factor: null,
						bias: null
					},
					fields: [
						// {
						// 	key: 'name',
						// 	component: EditFormTextField,
						// 	required: true
						// },
						{
							key: 'name',
							component: EditFormSelectField,
							required: true,
							type: 'enum',
							filterKey: '^aspect',
							items: aspectId => {
								if (aspectId != null)
									return new Promise(resolve => { storageAspectService.getOutputAttributesListSelection(aspectId).then(inputs => { resolve([...(_.isArray(inputs) ? inputs : [])]); }); });

								return new Promise(resolve => resolve([]));
							}
						},
						{
							key: 'type',
							component: EditFormSelectField,
							required: true,
							type: 'enum',
							items: [
								{ label: 'Constant', value: 'Constant' },
								{ label: 'Linear', value: 'Linear' },
								{ label: 'Exponential', value: 'Exponential' }
							]
						},
						{
							key: 'boundaryType',
							component: EditFormSelectField,
							required: true,
							type: 'enum',
							items: [
								{ label: 'Lower', value: 'Lower' },
								{ label: 'Upper', value: 'Upper' }
							]
						},
						{
							key: 'boundary',
							component: EditFormTextField,
							type: 'number'
						},
						{
							key: 'value',
							component: EditFormTextField,
							type: 'number',
							dependsOn: 'type',
							visibleIf: type => type === 'Constant'
						},
						{
							key: 'factor',
							component: EditFormTextField,
							type: 'number',
							dependsOn: 'type',
							visibleIf: type => (type === 'Linear' || type === 'Exponential')
						},
						{
							key: 'bias',
							component: EditFormTextField,
							type: 'number',
							dependsOn: 'type',
							visibleIf: type => (type === 'Linear' || type === 'Exponential')
						}
					]
				},
				{
					key: 'transformerLimits',
					tab: 'transformerLimits',
					component: EditFormGridComponent,
					defaultValue: {
						id: null,
						name: null,
						type: null,
						boundaryType: null,
						boundary: null,
						value: null,
						factor: null,
						bias: null
					},
					fields: [
						// {
						// 	key: 'name',
						// 	component: EditFormTextField,
						// 	required: true
						// },
						{
							key: 'name',
							component: EditFormSelectField,
							required: true,
							type: 'enum',
							filterKey: '^transformer',
							items: transformerId => {
								if (transformerId != null)
									return new Promise(resolve => { storageTransformerService.getOutputKeysSelection(transformerId).then(outputs => { resolve([...(_.isArray(outputs) ? outputs : [])]); }); });

								return new Promise(resolve => resolve([]));
							}
						},
						{
							key: 'type',
							component: EditFormSelectField,
							required: true,
							type: 'enum',
							items: [
								{ label: 'Constant', value: 'Constant' },
								{ label: 'Linear', value: 'Linear' },
								{ label: 'Exponential', value: 'Exponential' }
							]
						},
						{
							key: 'boundaryType',
							component: EditFormSelectField,
							required: true,
							type: 'enum',
							items: [
								{ label: 'Lower', value: 'Lower' },
								{ label: 'Upper', value: 'Upper' }
							]
						},
						{
							key: 'boundary',
							component: EditFormTextField,
							type: 'number'
						},
						{
							key: 'value',
							component: EditFormTextField,
							type: 'number',
							dependsOn: 'type',
							visibleIf: type => type === 'Constant'
						},
						{
							key: 'factor',
							component: EditFormTextField,
							type: 'number',
							dependsOn: 'type',
							visibleIf: type => (type === 'Linear' || type === 'Exponential')
						},
						{
							key: 'bias',
							component: EditFormTextField,
							type: 'number',
							dependsOn: 'type',
							visibleIf: type => (type === 'Linear' || type === 'Exponential')
						}
					]
				},
				{
					key: 'transformer_attributes',
					tab: 'transformer_attributes',
					component: EditFormGridComponent,
					defaultValue: {
						id: null,
						name: null,
						type: null,
						value: null
					},
					fields: [
						{
							key: 'name',
							component: EditFormSelectField,
							required: true,
							type: 'enum',
							filterKey: '^transformer',
							items: transformerId => {
								if (transformerId != null) {
									return new Promise(resolve => {
										storageTransformerService.getInputAttributesListSelection(transformerId).then(outputs => {
											resolve([...(_.isArray(outputs) ? outputs : [])]);
										});
									});
								}
								return new Promise(resolve => resolve([]));
							}
						},
						{
							key: 'type',
							component: EditFormSelectField,
							required: true,
							type: 'enum',
							items: [
								{ label: 'Variable', value: 'Variable' },
								{ label: 'ComponentMassFlow', value: 'ComponentMassFlow' },
								{ label: 'ComponentMassFrac', value: 'ComponentMassFrac' },
								{ label: 'SpreadSheet', value: 'SpreadSheet' }
							]
						},
						{
							key: 'value',
							component: EditFormTextField,
							required: true,
							type: 'number',
							dependsOn: 'type',
							visibleIf: type => type === 'Variable'
						},
						{
							key: 'value',
							component: EditFormComponentMassSomethingField,
							dependsOn: 'type',
							visibleIf: type => type === 'ComponentMassFrac' || type === 'ComponentMassFlow'
						}
					]
				}
			];

			return fields;
		}
	};
};

function OptimizationCaseEditForm() {
	const { id } = useParams();
	const dispatch = useDispatch();
	const [formData, setFormData] = useState(null);

	if (formData == null) {
		OptimizationCaseEditService.getFormData().then(resp => { setFormData(resp); });
		return <></>;
	}

	const myConfig = config({ id, formData });

	return (
		<EditFormPage
			config={myConfig}
			beforeSaveContent={
				<>
					<OptimCaseLoadAspectAttributes />
					<CreateOptimizerLogButton id={id} />
					<OptimizationCaseStateButton id={id} />
				</>
			}
		/>
	);
}

export default withRouter(OptimizationCaseEditForm);
